/**
 * Native WebView bridge — typed access to `window.POLYGRAF_NATIVE`.
 *
 * Per ADR-0001, the mobile shell wraps the web client in a WebView and injects
 * a `window.POLYGRAF_NATIVE` payload before page load. The shape of that payload
 * is a stable contract between the native shell and the bundled viewer.
 *
 * This module owns the single typed declaration of the payload and exposes
 * per-field accessors so consumers don't redeclare ad-hoc casts. Each accessor
 * is SSR-safe via a private `readNative()` that handles missing `window`.
 *
 * Adding a field is additive: optional, default `undefined`, and the matching
 * accessor returns `null` when absent.
 */
/**
 * Read the native payload, returning `null` when absent or when not running
 * in a browser (SSR / Node test). Private — callers go through the accessors.
 */
function readNative() {
    if (typeof window === 'undefined')
        return null;
    return window.POLYGRAF_NATIVE ?? null;
}
/**
 * `true` only when the native shell explicitly set `isNative: true`. Loose
 * presence-checks (`!!POLYGRAF_NATIVE`) intentionally do NOT pass — that
 * predicate had drifted across viewers before this module landed.
 */
export function isNativeWebView() {
    return readNative()?.isNative === true;
}
/** API base URL the native shell wants the viewer to hit, or `null` if absent. */
export function getNativeApiUrl() {
    return readNative()?.apiUrl ?? null;
}
export function getNativeSourceUrl() {
    return readNative()?.sourceUrl ?? null;
}
export function getNativeSourceName() {
    return readNative()?.sourceName ?? null;
}
export function getNativeSourceType() {
    return readNative()?.sourceType ?? null;
}
export function getNativeServiceId() {
    return readNative()?.serviceId ?? null;
}
export function getNativeUseBridge() {
    return readNative()?.useBridge === true;
}
export function getNativeFontScale() {
    return readNative()?.fontScale ?? null;
}
/**
 * Better Auth session token injected by the native shell, or `null` if absent
 * (anonymous / public source). The bundled viewer sends it as
 * `Authorization: Bearer <token>` on tRPC + media requests (M1).
 */
export function getNativeToken() {
    return readNative()?.token ?? null;
}
/**
 * Build the JavaScript snippet that initializes `window.POLYGRAF_NATIVE`
 * inside the WebView. Used as the `injectedJavaScriptBeforeContentLoaded`
 * value so the bundled viewer's mount can read the payload synchronously.
 *
 * The injected JS escapes the payload through `JSON.stringify` — never
 * concatenate user-controlled strings into the script body.
 */
export function serializeNativeBridge(payload) {
    // Strip the local `kind` discriminator before serialization — the
    // bundled viewer reads `sourceType` / `useBridge` / `serviceId` to
    // pick its render path, never `kind`. Keeping `kind` out of the
    // emitted JS avoids leaking the internal taxonomy.
    const { kind: _kind, ...rest } = payload;
    void _kind;
    return `window.POLYGRAF_NATIVE = ${JSON.stringify(rest)}; true;`;
}
//# sourceMappingURL=native-bridge.js.map